export default function Business() {
  const businessFeatures = [
    {
      icon: 'ri-building-2-line',
      title: 'Retail Stores',
      description: 'Comprehensive security for shops, boutiques, and retail chains with advanced surveillance and access control'
    },
    {
      icon: 'ri-hotel-line',
      title: 'Offices & Warehouses',
      description: 'Scalable solutions for corporate offices, industrial facilities, and storage locations'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Restaurants & Hospitality',
      description: 'Protect your guests and assets with discreet monitoring and smart security systems'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Healthcare Facilities',
      description: 'HIPAA-compliant security systems designed for medical offices and clinics'
    }
  ];

  const capabilities = [
    'Multi-location management',
    'Advanced video analytics',
    'Access control systems',
    'Intrusion detection',
    'Fire & life safety',
    'Remote monitoring',
    'Integration with existing systems',
    'Scalable infrastructure'
  ];

  const internetFeatures = [
    {
      icon: 'ri-speed-line',
      title: 'High-Speed Fiber',
      description: 'Lightning-fast fiber optic internet with symmetrical upload and download speeds for seamless connectivity'
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'Residential Solutions',
      description: 'Reliable home internet packages designed for streaming, gaming, remote work, and smart home devices'
    },
    {
      icon: 'ri-building-4-line',
      title: 'Business Internet',
      description: 'Enterprise-grade connectivity with guaranteed uptime, dedicated bandwidth, and priority support'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Round-the-clock technical support to keep your connection running smoothly without interruption'
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="business" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            COMMERCIAL SOLUTIONS
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Custom Security Systems <span className="text-teal-600">For Your Business</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Protect your business with enterprise-grade security solutions tailored to your specific needs. From small offices to large commercial facilities, we design and install custom systems that grow with your business.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {businessFeatures.map((feature, index) => (
            <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-xl mb-6">
                <i className={`${feature.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 md:p-16 mb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Enterprise-Grade Capabilities
              </h3>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Our commercial security systems are designed to meet the unique challenges of business environments. We provide comprehensive solutions that integrate seamlessly with your operations.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-200">{capability}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="text-center mb-6">
                <i className="ri-shield-check-line text-5xl text-teal-400 mb-4"></i>
                <h4 className="text-2xl font-bold text-white mb-2">Ready to Secure Your Business?</h4>
                <p className="text-gray-300 text-sm">
                  Schedule a free consultation and site assessment. Our security experts will design a custom solution for your location.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Call us directly</p>
                    <p className="font-semibold">(464) 264-1696</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email us</p>
                    <p className="font-semibold">info@redeyesystems.com</p>
                  </div>
                </div>

                <button 
                  onClick={() => scrollToSection('contact')}
                  className="whitespace-nowrap w-full py-4 bg-teal-600 text-white rounded-xl text-base font-semibold hover:bg-teal-700 transition-all cursor-pointer mt-6"
                >
                  Request Business Quote
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Internet and Fiber Solutions Section */}
        <div id="internet" className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
            CONNECTIVITY SOLUTIONS
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Internet and <span className="text-blue-600">Fiber Solutions</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Stay connected with our cutting-edge internet and fiber optic solutions. Whether you're a homeowner or business owner, we offer customized connectivity packages tailored to your specific needs and usage requirements.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {internetFeatures.map((feature, index) => (
            <div key={index} className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-blue-100">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-600 rounded-xl mb-6">
                <i className={`${feature.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center">
          <div className="max-w-3xl mx-auto">
            <i className="ri-wifi-line text-6xl text-white mb-6"></i>
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Find the Perfect Internet Plan for Your Needs
            </h3>
            <p className="text-blue-100 text-lg mb-8 leading-relaxed">
              From basic browsing to bandwidth-intensive business operations, we have flexible internet and fiber solutions designed to match your lifestyle or business requirements. Experience reliable, high-speed connectivity that keeps you connected to what matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => scrollToSection('contact')}
                className="whitespace-nowrap px-8 py-4 bg-white text-blue-600 rounded-xl text-base font-semibold hover:bg-blue-50 transition-all cursor-pointer"
              >
                Get Internet Quote
              </button>
              <a 
                href="tel:4642641696"
                className="whitespace-nowrap px-8 py-4 bg-blue-800 text-white rounded-xl text-base font-semibold hover:bg-blue-900 transition-all cursor-pointer inline-flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call (464) 264-1696
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 text-sm max-w-2xl mx-auto">
            <strong>Trusted by businesses across Chicago.</strong> We provide ongoing support, maintenance, and system upgrades to ensure your security infrastructure stays ahead of emerging threats.
          </p>
        </div>
      </div>
    </section>
  );
}